// Relative times and durations as messages (DESIGN_SYSTEM §2 rule 10; T-015 hand-over (4)): "Just now",
// "12 min ago", "2 h ago", "Yesterday", "2 days ago", then the absolute day ("Wed, Sep 16") from 7 days on.
// Pure (ENGINE-style): "now" is a parameter, never read here (D-020) — the apps pass `clock.now()`.
//
// The rule, pinned by the DEMO_STORY inbox at DEMO_NOW (Tue, Sep 22, 12:30 am America/Chicago):
//   < 60 s → Just now · < 60 min → "n min ago" · < 6 h, or the same calendar day on the device → "n h ago"
//   (10:30 pm the evening before reads "2 h ago") · the previous calendar day → "Yesterday" (9:40 am the day
//   before, 14.8 h back, reads "Yesterday") · < 7 days → "n days ago" · else the day itself.
// Calendar days are the DEVICE's (REQ-261 AC4 — alert times follow the device), counted by
// `calendarDaysBetween` — core's one copy of D-870's rule (a day count is calendar days in a named zone, never
// elapsed hours) — so DST days fall where they should.

import { accountToday, calendarDaysBetween } from '../periods/periods';
import type { IsoInstant, Message } from '../types/primitives';
import { message } from './keys';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function epochMs(instant: IsoInstant | number, what: string): number {
  const ms = typeof instant === 'number' ? instant : new Date(instant).getTime();
  if (!Number.isFinite(ms)) throw new RangeError(`${what}: "${String(instant)}" is not an instant`);
  return ms;
}

/**
 * `timeAgo(firedAt, clock.now(), deviceTimeZone)` → a `time.*` message; render it with `render`.
 * A future instant (clock skew) reads "Just now".
 */
export function timeAgo(
  instant: IsoInstant | number,
  now: IsoInstant | number,
  deviceTimeZone: string,
): Message {
  const then = epochMs(instant, 'timeAgo');
  const nowMs = epochMs(now, 'timeAgo');
  const elapsed = nowMs - then;
  if (elapsed < MINUTE) return message('time.just_now');
  if (elapsed < HOUR)
    return message('time.ago', {
      n: { t: 'count', v: Math.floor(elapsed / MINUTE), unit: 'minute' },
    });
  const daysBack = calendarDaysBetween(then, nowMs, deviceTimeZone);
  if (elapsed < 6 * HOUR || daysBack === 0)
    return message('time.ago', { n: { t: 'count', v: Math.floor(elapsed / HOUR), unit: 'hour' } });
  if (daysBack === 1) return message('time.yesterday');
  if (daysBack < 7) return message('time.ago', { n: { t: 'count', v: daysBack, unit: 'day' } });
  return message('time.on', { date: { t: 'date', v: accountToday(then, deviceTimeZone) } });
}

/**
 * A length of time as a `count` param: under an hour in minutes (at least 1 — "Next refresh in 1 min"), under a
 * day in hours, else in days. Rounded up, so a wait never reads shorter than it is.
 *
 * `duration` below is this param under `time.duration`; **`state.refresh_wait` takes the same param** (REQ-283
 * AC1's "says when the next refresh is possible" — `refreshState` in `@sl/core/queries`, T-208), so the rounding
 * rule has one home instead of one per sentence.
 */
export function durationCount(ms: number): { t: 'count'; v: number; unit: string } {
  if (!Number.isFinite(ms))
    throw new RangeError(`durationCount: ${String(ms)} is not a length of time`);
  const span = Math.max(0, ms);
  if (span < HOUR) return { t: 'count', v: Math.max(1, Math.ceil(span / MINUTE)), unit: 'minute' };
  if (span < DAY) return { t: 'count', v: Math.ceil(span / HOUR), unit: 'hour' };
  return { t: 'count', v: Math.ceil(span / DAY), unit: 'day' };
}

/**
 * A duration as a `time.duration` message: under an hour in minutes (at least 1 — "Next refresh in 1 min"),
 * under a day in hours, else in days. Rounded up, so a wait never reads shorter than it is.
 */
export function duration(ms: number): Message {
  if (!Number.isFinite(ms)) throw new RangeError(`duration: ${String(ms)} is not a length of time`);
  return message('time.duration', { n: durationCount(ms) });
}
